import { useContext } from "react";
import { ChevronRight, ExternalLink } from "lucide-react";
import { dateOnlyFormatted } from "../../../utils/systemSettings";
import { calculateDurationNoWeekends } from "../../dashboard/utils/date";
import { SharedTimelineContext } from "../SharedTimelineContext";
import useNavigation from "../../../utils/useNavigation";
import {
  CompletedTaskIcon,
  InProgressTaskIcon,
  SlippingTaskIcon,
  MonitoringTaskIcon,
  OnHoldTaskIcon,
} from "../../dashboard/icons/icons";

export default function Sidebar({
  reference,
  viewType,
  componentHeight,
  expandedProjects,
  toggleProject,
  entityType,
  sidebarWidth,
  columnWidths,
  hoveredRowId,
  setHoveredRowId,
  expandedScopes,
  toggleScope,
  expandedTaskGroups,
  toggleTaskGroup,
  onTaskClick,
}) {
  const { timelineData } = useContext(SharedTimelineContext);
  const { navigateTo } = useNavigation();
  const settings = JSON.parse(localStorage.getItem("settings"));

  // "program" and "projectTimeline" share the same 3-level rendering shape (see GanttContainer.jsx).
  const hasTaskLevel = entityType === "program" || entityType === "projectTimeline";

    const statusIconMap = {
    0: <InProgressTaskIcon style={{minWidth: '12px'}}/>,
    1: <MonitoringTaskIcon style={{minWidth: '12px'}}/>,
    2: <SlippingTaskIcon style={{minWidth: '12px'}}/>,
    3: <CompletedTaskIcon style={{minWidth: '12px'}}/>,
    4: <OnHoldTaskIcon style={{minWidth: '12px'}}/>
  }
  // Determine height calculation based on entity type
  const getHeightStyle = () => {
    // Portfolio/program/projectTimeline have an additional 40px for the target date events row
    // (Key Events strip), except in day view where that row never renders.
    const showsKeyEventsRow = (entityType === "portfolio" || hasTaskLevel) && viewType !== "day";
    const targetDateRowHeight = showsKeyEventsRow ? 40 : 0;

    if (viewType === "day") {
      return `calc(100vh - ${entityType === "portfolio" ? "246" : "305"}px)`;
    }
    return `calc(100vh - ${entityType === "portfolio" ? (271 + targetDateRowHeight) : (300 + targetDateRowHeight)}px)`;
  };

  // Get nested items based on entity type
  const getNestedItems = (item) => {
    if (entityType === "portfolio") {
      return item.projects || [];
    }
    // For program timeline, get scopeItems
    return item.scopeItems || item.scopes || [];
  };

  // Handle navigation for top-level items
  const handleTopLevelNavigation = (item) => {
    if (entityType === "portfolio") {
      // Portfolio -> Program
      navigateTo("Programs", item.id, item.name);
    } else if (entityType === "program") {
      // Program -> Project
      navigateTo("Projects", item.id, item.name);
    }
  };

  // Handle navigation for nested items
  const handleNestedItemNavigation = (nestedItem) => {
    if (entityType === "portfolio") {
      // Portfolio timeline: nested items are Projects
      navigateTo("Projects", nestedItem.id, nestedItem.name);
    } else if (hasTaskLevel) {
      // Program/ProjectTimeline: nested items are Scope Items
      navigateTo("ScopeItems", nestedItem.id, nestedItem.name);
    }
  };

  return (
    <div
      ref={reference}
      className="timeline-sidebar-wrapper overflow-y-auto overflow-x-scroll w-full"
      style={{
        height: getHeightStyle(),
        minWidth: sidebarWidth,
      }}
    >
      {timelineData.map((item, index) => (
        <div
          key={item.id}
          className={`${index % 2 === 0 ? "bg-white" : "bg-[#f5f5f5]"} border-b`}
        >
          {/* Top-level item (Program for portfolio, Project for program) */}
          <div
            onMouseEnter={() => setHoveredRowId(item.id)}
            onMouseLeave={() => setHoveredRowId(null)}
            className={`py-1 h-[52px] flex items-center ${
              hoveredRowId === item.id && "!bg-[#ece7fd]"
            }`}
          >
            <div
              className="flex px-2 justify-start gap-1 items-center"
              style={{
                width: columnWidths.scopes,
              }}
            >
              <button
                onClick={() => toggleProject(item.id)}
                className="transition-transform duration-200"
              >
                <ChevronRight
                  size={16}
                  className={`transform ${
                    expandedProjects[item.id] ? "rotate-90" : ""
                  }`}
                />
              </button>
              <span
                className={`font-bold truncate block capitalize cursor-pointer transition-all ${
                  hoveredRowId === item.id
                    ? "font-bold underline text-[#342C82]"
                    : ""
                }`}
                onClick={() => handleTopLevelNavigation(item)}
              >
                {item.name}
              </span>
            </div>
            <p
              className="text-s font-medium text-center"
              style={{
                minWidth: columnWidths.completed,
              }}
            >
              {item?.progress || 0}%
            </p>
            <p
              className="text-s font-medium text-center truncate"
              style={{
                minWidth: columnWidths.duration,
              }}
            >
              {item?.startDate && item?.endDate
                ? `${calculateDurationNoWeekends(item.startDate, item.endDate)} days`
                : "--"}
            </p>
            <p
              className="text-s px-2 font-medium text-center truncate"
              style={{
                minWidth: columnWidths.startDate,
              }}
            >
              {item?.startDate
                ? dateOnlyFormatted(settings, item.startDate)
                : "--"}
            </p>
            <p
              className="text-s px-2 font-medium text-center truncate"
              style={{
                minWidth: columnWidths.endDate,
              }}
            >
              {item?.endDate || item?.revisedEndDate
                ? dateOnlyFormatted(
                    settings,
                    item.revisedEndDate || item.endDate
                  )
                : "--"}
            </p>
          </div>

          {/* Nested items (Projects for portfolio, Scope Items for program) */}
          {expandedProjects[item.id] &&
            getNestedItems(item)?.map((nestedItem, i) => (
              <div key={nestedItem.id}>
                {/* Scope / Project nested item row */}
                <div
                  onMouseEnter={() => setHoveredRowId(nestedItem.id)}
                  onMouseLeave={() => setHoveredRowId(null)}
                  className={`h-[52px] py-1 cursor-pointer flex flex-row items-center justify-between ${
                    hoveredRowId === nestedItem.id && "bg-[#ece7fd]"
                  }`}
                >
                  <div className="flex items-center w-full">
                    <div
                      className={`flex items-center gap-2 ${hasTaskLevel ? "pl-4 gap-1" : "pl-8"}`}
                      style={{
                        width: columnWidths.scopes,
                      }}
                    >
                      {hasTaskLevel && (
                        (nestedItem.tasks?.length > 0 || nestedItem.taskGroups?.length > 0) ? (
                          <button
                            onClick={(e) => { e.stopPropagation(); toggleScope(nestedItem.id); }}
                            className="transition-transform duration-200 flex-shrink-0"
                          >
                            <ChevronRight
                              size={14}
                              className={`transform ${expandedScopes?.[nestedItem.id] ? "rotate-90" : ""}`}
                            />
                          </button>
                        ) : (
                          <span className="w-[14px] flex-shrink-0" />
                        )
                      )}
                      {statusIconMap[nestedItem.status]}
                      <span
                        className={`text-s font-bold truncate capitalize cursor-pointer transition-all ${
                          hoveredRowId === nestedItem.id
                            ? "underline text-[#342C82]"
                            : ""
                        }`}
                        onClick={() => handleNestedItemNavigation(nestedItem)}
                      >
                        {nestedItem.name}
                      </span>
                    </div>
                    <p
                      className="text-s font-medium text-center truncate"
                      style={{
                        minWidth: columnWidths.completed,
                      }}
                    >
                      {nestedItem.progress || 0}%
                    </p>
                    <p
                      className="text-s font-medium text-center truncate"
                      style={{
                        minWidth: columnWidths.duration,
                      }}
                    >
                      {nestedItem?.startDate && nestedItem?.endDate
                        ? `${calculateDurationNoWeekends(
                            nestedItem.startDate,
                            nestedItem.endDate
                          )} days`
                        : "--"}
                    </p>
                    <p
                      className="text-s px-2 font-medium text-center truncate"
                      style={{
                        minWidth: columnWidths.startDate,
                      }}
                    >
                      {nestedItem?.startDate
                        ? dateOnlyFormatted(settings, nestedItem.startDate)
                        : "--"}
                    </p>
                    <p
                      className="text-s px-2 font-medium text-center truncate"
                      style={{
                        minWidth: columnWidths.endDate,
                      }}
                    >
                      {nestedItem?.endDate || nestedItem?.revisedEndDate
                        ? dateOnlyFormatted(
                            settings,
                            nestedItem.revisedEndDate || nestedItem.endDate
                          )
                        : "--"}
                    </p>
                  </div>
                </div>

                {/* Task Group + Task rows — only when scope is expanded */}
                {hasTaskLevel && expandedScopes?.[nestedItem.id] && (() => {
                  const renderTaskRow = (task, indentClass) => (
                    <div
                      key={task.id}
                      onMouseEnter={() => setHoveredRowId(task.id)}
                      onMouseLeave={() => setHoveredRowId(null)}
                      className={`h-[20px] flex flex-row items-center cursor-pointer ${
                        hoveredRowId === task.id && "bg-[#ece7fd]"
                      }`}
                      onClick={() => onTaskClick({
                        taskId: task.id,
                        scopeID: nestedItem.id,
                        scopeName: nestedItem.name,
                        projectID: item.id,
                        projectName: item.name,
                      })}
                    >
                      <div className="flex items-center w-full">
                        <div
                          className={`flex items-center gap-2 ${indentClass}`}
                          style={{ width: columnWidths.scopes }}
                        >
                          {statusIconMap[task.status]}
                          <span
                            className={`text-s font-medium truncate capitalize transition-all ${
                              hoveredRowId === task.id
                                ? "font-bold underline text-[#342C82]"
                                : ""
                            }`}
                          >
                            {task.name}
                          </span>
                        </div>
                        <p
                          className="text-s font-medium text-center truncate"
                          style={{ minWidth: columnWidths.completed }}
                        >
                          {task.progress || 0}%
                        </p>
                        <p
                          className="text-s font-medium text-center truncate"
                          style={{ minWidth: columnWidths.duration }}
                        >
                          {task?.startDate && task?.endDate
                            ? `${calculateDurationNoWeekends(task.startDate, task.endDate)} days`
                            : "--"}
                        </p>
                        <p
                          className="text-s px-2 font-medium text-center truncate"
                          style={{ minWidth: columnWidths.startDate }}
                        >
                          {task?.startDate ? dateOnlyFormatted(settings, task.startDate) : "--"}
                        </p>
                        <p
                          className="text-s px-2 font-medium text-center truncate"
                          style={{ minWidth: columnWidths.endDate }}
                        >
                          {task?.endDate ? dateOnlyFormatted(settings, task.endDate) : "--"}
                        </p>
                      </div>
                    </div>
                  );

                  return (
                    <>
                      {(nestedItem.taskGroups || []).map((group) => (
                        <div key={group.id}>
                          <div
                            onMouseEnter={() => setHoveredRowId(group.id)}
                            onMouseLeave={() => setHoveredRowId(null)}
                            className={`h-[52px] py-1 flex flex-row items-center ${
                              hoveredRowId === group.id && "bg-[#ece7fd]"
                            }`}
                          >
                            <div className="flex items-center w-full">
                              <div
                                className="flex items-center gap-2 pl-10 gap-1"
                                style={{ width: columnWidths.scopes }}
                              >
                                {group.tasks?.length > 0 ? (
                                  <button
                                    onClick={(e) => { e.stopPropagation(); toggleTaskGroup(group.id); }}
                                    className="transition-transform duration-200 flex-shrink-0"
                                  >
                                    <ChevronRight
                                      size={14}
                                      className={`transform ${expandedTaskGroups?.[group.id] ? "rotate-90" : ""}`}
                                    />
                                  </button>
                                ) : (
                                  <span className="w-[14px] flex-shrink-0" />
                                )}
                                <span className="text-s font-bold truncate capitalize">
                                  {group.name}
                                </span>
                              </div>
                              <p
                                className="text-s font-medium text-center truncate"
                                style={{ minWidth: columnWidths.completed }}
                              >
                                {group.progress || 0}%
                              </p>
                              <p
                                className="text-s font-medium text-center truncate"
                                style={{ minWidth: columnWidths.duration }}
                              >
                                {group?.startDate && group?.endDate
                                  ? `${calculateDurationNoWeekends(group.startDate, group.endDate)} days`
                                  : "--"}
                              </p>
                              <p
                                className="text-s px-2 font-medium text-center truncate"
                                style={{ minWidth: columnWidths.startDate }}
                              >
                                {group?.startDate ? dateOnlyFormatted(settings, group.startDate) : "--"}
                              </p>
                              <p
                                className="text-s px-2 font-medium text-center truncate"
                                style={{ minWidth: columnWidths.endDate }}
                              >
                                {group?.endDate ? dateOnlyFormatted(settings, group.endDate) : "--"}
                              </p>
                            </div>
                          </div>
                          {expandedTaskGroups?.[group.id] &&
                            group.tasks?.map((task) => renderTaskRow(task, "pad-left-16"))}
                        </div>
                      ))}
                      {nestedItem.tasks?.map((task) => renderTaskRow(task, "pl-10"))}
                    </>
                  );
                })()}
              </div>
            ))}
        </div>
      ))}
    </div>
  );
}
